/**
 * AnswerReceivedBanner — slim dismissible banner above the chat: "📥 Answer
 * received — <target> responded to "<title>"". Clicking it opens the asks
 * (the host decides where they live) and marks the answer seen. The ✕ hides
 * it locally without marking seen (the NEW badge on the card persists).
 * Re-arms itself when a different request needs attention.
 */
import { useState } from 'react';

import {
  markDataRequestSeen,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import styles from './AnswerReceivedBanner.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface AnswerReceivedBannerProps {
  request: DoorDataRequestRead;
  /** Reveal the asks (the banner marks the answer seen itself). */
  onOpenRequests: () => void;
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
}

export function AnswerReceivedBanner({
  request,
  onOpenRequests,
  onRequestUpdated,
}: AnswerReceivedBannerProps) {
  const [hiddenFor, setHiddenFor] = useState<string | null>(null);
  // A read-only viewer's look never marks the desk's answer seen (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  if (hiddenFor === request.id) return null;

  const open = () => {
    onOpenRequests();
    if (readOnly) return;
    markDataRequestSeen(request.id)
      .then(onRequestUpdated)
      .catch(() => undefined);
  };

  return (
    <div className={styles.banner} role="status">
      <button type="button" className={styles.bannerBody} onClick={open}>
        <span aria-hidden="true">📥</span>
        <span>
          <strong>Answer received</strong> — {request.target_name} responded to
          {' '}
          “{request.question_title}”
        </span>
      </button>
      <button
        type="button"
        className={styles.bannerClose}
        aria-label="Dismiss answer notice"
        onClick={() => setHiddenFor(request.id)}
      >
        ✕
      </button>
    </div>
  );
}

export default AnswerReceivedBanner;
